import { useEffect, useState } from "react";

import type { CartPayload } from "./types";
import { usePatchCart } from "./hooks";
import { formatRangePretty, inclusiveDays, isIsoDate, weeksBilled } from "./dateMath";

// Capture rental dates at the cart stage — the audit's #6 critique was
// that the page sends the customer into checkout with no number to react
// to. With dates the sidebar can compute weeks × weekly rate per line,
// turning "from $X / week" into "estimated $Y for 2 weeks." Backend
// already accepts `delivery_date_start` / `delivery_date_end` via
// PATCH /api/v1/cart (Api::V1::CartController#cart_params).
export interface CartDatesSectionProps {
  payload: CartPayload;
}

export function CartDatesSection({ payload }: CartDatesSectionProps): JSX.Element {
  const patch = usePatchCart();
  const [start, setStart] = useState<string>(payload.delivery_date_start ?? "");
  const [end, setEnd] = useState<string>(payload.delivery_date_end ?? "");

  // Reconcile local state when the cart payload changes underneath us
  // (e.g. another tab updated dates, or the cart refetches after a
  // promo apply). Avoids stale optimistic state across long-lived
  // mounts.
  useEffect(() => {
    setStart(payload.delivery_date_start ?? "");
    setEnd(payload.delivery_date_end ?? "");
  }, [payload.delivery_date_start, payload.delivery_date_end]);

  const days = inclusiveDays(start, end);
  const weeks = weeksBilled(days);

  function commit(next: { start?: string | undefined; end?: string | undefined }): void {
    const nextStart = next.start ?? start;
    const nextEnd = next.end ?? end;
    // Only commit when both sides parse cleanly. Single-side edits
    // shouldn't fire a network call yet — wait until the customer
    // picks the other date.
    if (!isIsoDate(nextStart) || !isIsoDate(nextEnd)) return;
    if (nextStart === payload.delivery_date_start && nextEnd === payload.delivery_date_end) return;
    patch.mutate({ delivery_date_start: nextStart, delivery_date_end: nextEnd });
  }

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <section
      aria-labelledby="cart-dates-heading"
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm"
      data-testid="cart-dates-section"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 id="cart-dates-heading" className="text-base font-semibold text-brand-black">
            Rental dates
          </h2>
          <p className="text-xs text-zinc-500">
            We&rsquo;ll confirm the delivery window at checkout.
          </p>
        </div>
        {days && weeks ? (
          <p className="text-sm text-zinc-700" data-testid="cart-dates-duration">
            <span className="font-semibold text-brand-black">{formatRangePretty(start, end)}</span>
            <span className="ml-2 text-zinc-500">
              · {days} {days === 1 ? "day" : "days"} · billed as {weeks} {weeks === 1 ? "week" : "weeks"}
            </span>
          </p>
        ) : null}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <DateInput
          id="cart-date-start"
          label="Pickup / delivery date"
          value={start}
          min={todayIso}
          onChange={(value) => {
            setStart(value);
            commit({ start: value });
          }}
        />
        <DateInput
          id="cart-date-end"
          label="Return date"
          value={end}
          min={start || todayIso}
          onChange={(value) => {
            setEnd(value);
            commit({ end: value });
          }}
        />
      </div>
    </section>
  );
}

function DateInput({
  id,
  label,
  value,
  min,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  min: string;
  onChange: (next: string) => void;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-brand-black">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red"
      />
    </div>
  );
}
